import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CalendarDays, Trophy, CheckCircle2 } from 'lucide-react';
import DesktopHeader from '../components/DesktopHeader';
import BottomNav from '../components/BottomNav';
import { useApp } from '../context/AppContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authCfg = () => {
  const token = localStorage.getItem('c24_token');
  return {
    withCredentials: true,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  };
};

const Subscriptions = () => {
  const nav = useNavigate();
  const { user } = useApp();
  const [plans, setPlans] = useState([]);
  const [mySub, setMySub] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [pRes, sRes] = await Promise.all([
        axios.get(`${API}/subscriptions/plans`),
        user ? axios.get(`${API}/subscriptions/mine`, authCfg()) : Promise.resolve({ data: {} }),
      ]);
      setPlans(pRes.data);
      setMySub(sRes.data && sRes.data.id ? sRes.data : null);
    } catch (e) { console.warn(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user]);

  const subscribe = async (planId) => {
    if (!user) { nav('/login'); return; }
    try {
      const { data } = await axios.post(`${API}/subscriptions/subscribe`, { plan_id: planId }, authCfg());
      setMySub(data);
    } catch (e) {
      if (e?.response?.status === 401) { nav('/login'); return; }
      alert(e.response?.data?.detail || 'Subscription failed');
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 pb-24 md:pb-8">
      <DesktopHeader />

      <div className="md:hidden sticky top-0 z-30 bg-white border-b border-neutral-100">
        <div className="px-5 py-4 flex items-center gap-3">
          <button onClick={() => nav(-1)} className="w-9 h-9 rounded-full bg-neutral-100 flex items-center justify-center">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <h1 className="text-lg font-extrabold">Subscriptions</h1>
            <p className="text-xs text-neutral-500">Save more with regular cleaning packages</p>
          </div>
        </div>
      </div>

      <div className="max-w-[900px] mx-auto px-5 md:px-6 pt-4 md:pt-8">
        <div className="hidden md:block mb-5">
          <h1 className="text-2xl font-extrabold">Subscriptions</h1>
          <p className="text-sm text-neutral-500 mt-0.5">Save more with regular cleaning packages</p>
        </div>

        {/* Active subscription banner */}
        {mySub && (
          <div className="rounded-2xl p-5 text-white mb-4" style={{ background: '#2E7D32' }}>
            <span className="inline-flex items-center gap-1.5 bg-white/20 text-xs font-bold px-2.5 py-1 rounded-full">
              <CheckCircle2 className="w-3.5 h-3.5" /> ACTIVE
            </span>
            <p className="mt-3 text-2xl font-extrabold">{mySub.plan_name}</p>
            <p className="text-sm opacity-90 mt-1">{mySub.visits_used} of {mySub.visits_total} visits used</p>
            <p className="text-xs opacity-80 mt-0.5">Renews on {mySub.renews_on}</p>
          </div>
        )}

        {/* Plan cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
          {plans.map((p) => {
            const isCurrent = mySub && mySub.plan_id === p.id;
            const isGold = p.premium;
            return (
              <div key={p.id} className={`rounded-2xl overflow-hidden flex flex-col ${isGold ? 'border-2 border-[#EA580C] bg-[#FFFBEB]' : 'bg-white border border-neutral-200'}`}>
                <div className="p-4 flex-1">
                  {isGold && (
                    <span className="inline-block bg-[#EA580C] text-white text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wide mb-2">Premium</span>
                  )}
                  <div className="flex items-start gap-3">
                    <div className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 ${isGold ? 'bg-[#FEF3C7]' : 'bg-[#E8F5E9]'}`}>
                      {isGold ? <Trophy className="w-5 h-5 text-[#D97706]" /> : <CalendarDays className="w-5 h-5 text-[#2E7D32]" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-base leading-tight">{p.name}</p>
                      <p className="text-sm text-neutral-600 mt-0.5">{p.description}</p>
                    </div>
                  </div>
                  <div className="mt-4 flex items-baseline gap-1">
                    <span className="text-2xl font-extrabold text-[#2E7D32]">₹{p.price}</span>
                    <span className="text-xs text-neutral-500">/{p.period}</span>
                  </div>
                </div>
                <button onClick={() => isCurrent ? null : nav(`/subscription/${p.id}`)} disabled={isCurrent}
                  data-testid={`subscription-view-${p.id}-btn`}
                  className={`w-full py-3.5 font-semibold text-sm transition-colors ${isCurrent
                    ? 'bg-neutral-200 text-neutral-500 cursor-not-allowed'
                    : isGold
                      ? 'bg-[#EA580C] hover:bg-[#C2410C] text-white'
                      : 'bg-[#2E7D32] hover:bg-[#276D2A] text-white'}`}>
                  {isCurrent ? 'Current plan' : 'View details & subscribe'}
                </button>
              </div>
            );
          })}
          {loading && <p className="text-center text-sm text-neutral-500 py-4 md:col-span-3">Loading plans…</p>}
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default Subscriptions;
